import { PIECES } from '../../data';
import { COUNTED, PRELUDE_ENTRIES, SONATA_ENTRIES, VARIATION_ENTRIES } from '../../data/chorales/corpusIndex';
import ALIGNMENT from '../../data/chorales/alignment-report.json';
import { WEIGHTS } from '../../music/tension';
import { DEPTH_LABELS } from '../copy';
import type { Depth } from '../hooks';

export function Method() {
  const piece = PIECES.bwv846;
  return (
    <div className="prose">
      <header className="page-head">
        <span className="label">About</span>
        <h1>How to use the atlas</h1>
        <p>
          The atlas lets you hear harmony before you name it. Everything you see and hear is produced by explicit rules from encoded scores and published analyses; the
          rules are written down below. No machine-learning model runs in this site.
        </p>
      </header>

      <h2>Where to start</h2>
      <ul>
        <li>
          <b>Pieces</b>: open any piece and press play. <b>Listen</b> shows one line, the pull of the harmony, drawn as you hear it. <b>Map</b> shows the whole piece at
          once: its keys, where its phrases end, and the patterns it walks through. The <b>?</b> on a piece explains the page, and <b>What am I seeing?</b> under the map
          explains each row.
        </li>
        <li>
          <b>Compare</b>: rooms that play one passage next to another version of itself: one tune under different harmonies, a theme and its variations, a sonata theme
          when it returns. Click any cell to hear it; the big button always says what it will play.
        </li>
        <li>
          <b>Patterns</b>: cadences, sequences and stock progressions, each playable on its own and in real music. Browse them by name or by what they feel like.
        </li>
        <li>
          <b>Statistics</b>: what the composers do, counted. Pick a row in any chart to hear the moments it counts.
        </li>
        <li>
          <b>Play</b>: an instrument. Tap chords on the gravity well; the atlas voices them and names the pattern you played. <b>Train your ear</b>, next to it, asks
          short listening questions made from the pieces.
        </li>
        <li>
          <b>Search</b> (the magnifier at the top, or press <kbd>/</kbd>): any piece by title, BWV or K. number, tune or key (“prelude in B minor”), and any pattern,
          feeling or page. Arrow keys choose, Enter opens.
        </li>
      </ul>

      <h2>On a piece</h2>
      <ul>
        <li>
          <b>What if…</b> changes exactly one thing and plays it; switch between the original and the change while it plays.
        </li>
        <li>
          <b>Guess what comes next</b> stops just before a resolution. Choose where the music should go, then hear what the composer wrote.
        </li>
        <li>
          <b>Hear</b> strips the music down: chords only, melody and bass, the bass alone, or only the home note of each passage.
        </li>
        <li>
          The <b>moving score</b> under the play button is the edition's own notation, engraved on one long system that moves under the orange line (now): staves,
          voices, note values, beams, rests, ties, stems, clefs, key and time signatures, repeats, fermatas, grace notes and tuplets come from the source score, and
          accidentals, spacing and the placement of everything follow standard engraving practice. Clef and key signature stay pinned at the left; the band underneath is
          the tension of each chord. Drag it to move through the piece, tap it to jump. When a score and its playback do not line up note for note (a few chorales with
          instrumental parts), or when you hear a simplified layer or a what-if, a plainer proportional view is shown instead. The score button hides it.
        </li>
        <li>Keyboard: space plays and pauses, F switches between Listen and Map, ← and → move by bar. Drag across the line to loop a passage.</li>
      </ul>

      <h2>How much theory</h2>
      <p>The Theory button at the top of every page sets how many names you see:</p>
      <ul>
        {([0, 1, 2, 3, 4] as Depth[]).map((d) => (
          <li key={d}>
            <b>{DEPTH_LABELS[d].short}</b>: {DEPTH_LABELS[d].long}
          </li>
        ))}
      </ul>

      <h2>How tension is computed</h2>
      <p>
        For each harmony the engine adds five ingredients, each computed from the notes and the harmonic label, and clips the sum to the range 0–1. The curve is then drawn
        smoothly through two points per harmony.
      </p>
      <div className="table-wrap">
        <table className="weights">
          <thead>
            <tr>
              <th>Ingredient</th>
              <th>What it measures</th>
              <th>Weight</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Function</td>
              <td>How strongly the chord points elsewhere: tonic 0, other tonic-family chords 0.14, predominants 0.34–0.45, dominants 0.62–0.84, applied dominants +0.04, cadential ⁶₄ 0.75.</td>
              <td className="num">× {WEIGHTS.fn}</td>
            </tr>
            <tr>
              <td>Dissonance</td>
              <td>Rough intervals among the sounding pitch classes: semitone 1, tritone 0.8, whole tone 0.45; normalised so a diminished seventh scores 1.</td>
              <td className="num">× {WEIGHTS.dissonance}</td>
            </tr>
            <tr>
              <td>Key distance</td>
              <td>Steps around the circle of fifths between the local key and home.</td>
              <td className="num">
                {WEIGHTS.distancePerFifth} per step, max {WEIGHTS.distanceCap}
              </td>
            </tr>
            <tr>
              <td>Colour</td>
              <td>Notes foreign to the local key ({WEIGHTS.chromaticPerNote} each), notes foreign to the chord such as suspensions ({WEIGHTS.nonChordTone} each), and the instability of a ⁶₄ chord ({WEIGHTS.sixFour}).</td>
              <td className="num">additive</td>
            </tr>
            <tr>
              <td>Pedal</td>
              <td>A held dominant bass adds {WEIGHTS.dominantPedalPerBar} per bar (max {WEIGHTS.dominantPedalCap}); a held tonic halves the pull of the chords above it and removes {WEIGHTS.tonicPedalPerBar} per bar.</td>
              <td className="num">additive</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="muted">
        The model is a transparent approximation in the spirit of published tonal-tension models (for example Lerdahl and Krumhansl’s). It will be calibrated against
        listener ratings before it is used for anything beyond orientation.
      </p>

      <h2>How the voices are chosen</h2>
      <p>
        When the site plays a chord that is not in a score (in the Walk, the Hold options, the rule of the octave), a voice-leading engine searches every possible voicing
        and chooses the one that moves least while keeping root, third and seventh, resolving leading tones up and sevenths down, and avoiding parallel fifths and octaves.
        The search is exhaustive and deterministic.
      </p>

      <h2>How the chorales are read</h2>
      <p>
        No chorale page is written by hand. A script reads each score and its published Roman-numeral analysis, and everything else on the page follows by rule, so every
        chorale can be checked the same way.
      </p>
      <ul>
        <li>
          <strong>Alignment.</strong> Each analysis is scored against the notes: the share of sounding time whose pitches belong to the labelled chord. Several readings are
          tried (the keys moved by any of twelve transpositions, the bars numbered as written or laid end to end, a repeat taken twice when the score writes it out) and
          the best is kept only if it is clearly better than the plain one. Chorales still below 80% are left out: {ALIGNMENT.excluded.length} of {ALIGNMENT.included + ALIGNMENT.excluded.length} so far.
        </li>
        <li>
          <strong>Phrases and punctuation.</strong> Every fermata ends a phrase (where a score encodes none, the analyst’s double bars are used). The chord under it and the one before decide the punctuation: dominant to tonic in root
          position is an authentic cadence (perfect when the melody lands on the tonic), dominant to vi is deceptive, IV to I is plagal, an arrival on V is a half cadence
          (Phrygian when it comes from iv⁶ in minor).
        </li>
        <li>
          <strong>Regions and paths.</strong> Key regions come from the analysis. Prinner, Romanesca, Fonte, Monte, falling-fifths sequences and the other paths are found by
          the same matcher the Walk uses, and each one found is listed on its path page.
        </li>
        <li>
          <strong>What-ifs and holds.</strong> At an authentic cadence the arrival chord is replaced by vi (VI in minor) under the same melody note, voiced by the engine. The
          hold offers Bach’s chord, vi and IV, then plays Bach’s continuation.
        </li>
        <li>
          <strong>Words.</strong> Most encoded scores carry the hymn’s first stanza, or another text sung to the same tune, rather than the stanza Bach set. Words are
          shown only when they can be the stanza actually sung.
        </li>
      </ul>

      <h2>How the sonatas are read</h2>
      <p>
        The Mozart movements follow the chorale method. Their analysts marked the form (“exposition”, “second theme”, “development”, “recapitulation”), and those labels
        become the sections of each page. Line endings come from the phrase annotations of the DCML Mozart corpus and are classified by the same cadence rules as the
        chorales’ fermatas. The words for each section are built from what the rules find there: the keys it visits, and whether the return begins at home. That is how
        K. 545 is heard to come home “through the wrong door”, in F major. The movements are laid out without repeats. Their scores are licensed for non-commercial use
        only, and a build switch leaves them out.
      </p>

      {PRELUDE_ENTRIES.length > 0 && (
        <>
          <h2>How the preludes are read</h2>
          <p>
            The other {PRELUDE_ENTRIES.length} preludes of the Well-Tempered Clavier, Book I, are read by the same rules as the sonatas, from analyses by Mark Gotham and
            colleagues. Those analyses mark no phrases, so the pages find them from the harmony: a phrase ends where a dominant in root position resolves to the tonic on a
            strong beat, and, in long stretches of figuration, just before the music changes key. Bach wrote no tempo marks; the listening speed of each prelude is an
            editorial choice, and the tempo control scales it.
          </p>
        </>
      )}

      <h2>How the variation sets are read</h2>
      <p>
        The {VARIATION_ENTRIES.length} sets of variations by Mozart and Beethoven come from TAVERN, a corpus made for studying exactly this: each set is cut into phrases,
        and every phrase carries its notes and a Roman-numeral analysis, beat by beat. Each phrase is found in the full score by its notes: its opening notes are matched
        against the score near where the previous phrase ended, and its numerals are placed at the same times. That gives the harmony, where each variation starts and
        where each phrase ends, whatever the bar numbering. Line endings are classified by the same cadence rules as the chorales. Where the edition’s playback marks give a
        variation its own speed (an Adagio before the finale, say), the page plays it at that speed relative to the theme; otherwise the whole set keeps one speed, which
        the tempo control scales.
      </p>

      <h2>How exposition and recapitulation are compared</h2>
      <p>
        Every bar is reduced to its top and bass notes, onset by onset. Two bars are compared under the best transposition, and the exposition and recapitulation are lined
        up bar by bar, allowing bars to be dropped or added, much as the tune rooms line up melodies. A pair that matches without transposition returns as it was; one that
        matches after moving into the home key is brought home, even if it changes mode on the way; a weaker match is counted as rewritten. The alignment reads left to
        right, so when themes come back in reverse order (K. 311) they appear as bars cut and added.
      </p>

      <h2>How refrains and variations are compared</h2>
      <p>
        In a rondo the refrain comes back several times; in a set of variations the theme does. Each return is lined up bar by bar under the first statement, in the same way
        as exposition and recapitulation, but every bar is compared twice: by its top and bass notes (does the surface come back?) and by its chords, beat by beat (does the
        harmony come back?). A bar whose notes return is heard as the first time, or the same in another key; a bar whose notes change over the same chords is a new surface
        on the same harmony, which is what most variations are; a bar where both change is rewritten. A chord that keeps its root but changes mode, as in a minor variation,
        counts most of the way as the same harmony.
      </p>

      <h2>How the tune rooms line settings up</h2>
      <p>
        Settings of one tune differ in key, metre and rhythm, and Bach sometimes decorates the melody, so they cannot be lined up by time. Instead their melodies are
        aligned note by note, the way two spellings of a word are compared: the best transposition is found, then each note of the reference melody is paired with a
        note of the other setting, allowing for extra passing notes and the odd changed note, with a bonus when two fermatas meet. The chord sounding under each paired
        note is what the room shows. Two chords count as different when their root or quality differs at the common pitch; inversions and sevenths do not count.
      </p>

      <h2>How the listening questions are made</h2>
      <p>
        Every question in <a href="#train">Train your ear</a> plays real music from the atlas, and every right answer is what the analyses and this site’s rules say; the
        only chance involved is which excerpt comes next. <b>How does it end?</b> plays the approach to a chorale fermata and stops two beats after it, moved to C major or
        A minor so the key gives nothing away. <b>Bright or dark?</b> plays the first line of a chorale whose first line ends in its home key. <b>Same harmony or
        not?</b> plays one melody note from two settings of the same tune, at one pitch, and compares every chord heard under the note, as the tune rooms do.{' '}
        <b>Which pattern?</b> plays a pattern found by rule, with a beat or two of lead-in, at the piece’s own speed. Scores stay in this browser.
      </p>

      <h2>How the observatory counts</h2>
      <ul>
        <li>
          Every count keeps the moments it counts, so each bar can be played. Nothing is sampled or estimated: all {COUNTED.length} chorales that pass the alignment gate are read
          whole, and the setting derived by transposition (BWV 244.17) is not counted twice.
        </li>
        <li>
          Keys are named from home (in F major, D minor is “vi”) so that chorales in different keys can be counted together. “What comes next” counts the next{' '}
          <em>different</em> chord in the same key, with inversions and sevenths folded into the chord (V⁶₅ counts as V).
        </li>
        <li>
          The breath of a line averages this site’s tension model over each phrase at 24 even steps. It shows a shape, not a law: it is only as good as the model and the
          analyses.
        </li>
        {SONATA_ENTRIES.length > 0 && (
          <li>
            The same counts can be made for the {SONATA_ENTRIES.length} Mozart movements, by the same rules. There a “line” is a phrase closed in the DCML phrase annotations
            rather than a fermata, so the two corpora are comparable in kind but not unit for unit: a chorale line is sung in one breath, a sonata phrase can be much longer.
          </li>
        )}
        <li>The corpus index behind the Library, the path pages and the observatory is computed when the site is built, by the same rules as the chorale pages; a test fails if the two ever disagree.</li>
      </ul>

      <h2>Where the data comes from</h2>
      <ul>
        {piece.sources.map((s) => (
          <li key={s.label}>
            {s.url ? <a href={s.url}>{s.label}</a> : s.label}
            {s.licence ? ` — ${s.licence}` : ''}
          </li>
        ))}
        <li>{piece.editorial}</li>
        <li>
          Bach chorales: scores from the <a href="https://github.com/cuthbertLab/music21">music21 corpus</a>; analyses from{' '}
          <a href="https://github.com/MarkGotham/When-in-Rome">When in Rome</a>, mostly by Dmitri Tymoczko, with Andrew Jones and fourteen other analysts; proofread by
          Hamish Robb, Luis Valencia and Dmitri Tymoczko (CC BY-SA 4.0). Each chorale page names its analyst.
        </li>
        {SONATA_ENTRIES.length > 0 && (
          <li>
            Mozart piano sonatas: scores and analyses from the <a href="https://github.com/DCMLab/mozart_piano_sonatas">DCML Mozart piano sonatas corpus</a> (Hentschel,
            Neuwirth and Rohrmeier), via the When in Rome conversions (CC BY-NC-SA 4.0, non-commercial use only).
          </li>
        )}
        {PRELUDE_ENTRIES.length > 0 && (
          <li>
            The Well-Tempered Clavier, Book I, preludes 2–24: scores from the <a href="https://github.com/MarkGotham/When-in-Rome">When in Rome</a> corpus (no licence stated;
            left out of the commercial build), analyses by Mark Gotham and colleagues (CC BY-SA 4.0).
          </li>
        )}
        {VARIATION_ENTRIES.length > 0 && (
          <li>
            Variations by Mozart and Beethoven: scores, phrases and Roman numerals from <a href="https://github.com/jcdevaney/TAVERN">TAVERN</a>, Theme And Variation
            Encodings with Roman Numerals (Johanna Devaney, Claire Arthur, Nathaniel Condit-Schultz and Kirsten Nisula, 2015), annotator A (CC BY-SA 4.0).
          </li>
        )}
        <li>Automated tests check that every harmony label explains the notes under it, that every what-if still makes harmonic sense, and that path prototypes avoid parallel fifths and octaves.</li>
      </ul>

      <h2>What AI did, and did not do</h2>
      <p>
        AI was used to help write code and draft texts during construction. Every musical claim shown here is either computed from the notes by the rules above or written
        into the data files, where it can be reviewed and corrected. Nothing is generated while you use the site.
      </p>

      <h2>Sound</h2>
      <p>Tones are synthesised in your browser from fixed harmonic spectra; no audio files are downloaded. The room reverberation uses a seeded, repeatable impulse response.</p>

      <h2>Next</h2>
      <ul>
        <li>More pieces from the eighteenth-century core: the rest of the Well-Tempered Clavier, Corelli trio sonatas, Haydn and Beethoven sonata movements.</li>
        <li>Recorded performances aligned to the scores, so the same moment can be heard in several interpretations.</li>
        <li>Open contributions of analyses, with versions, review and sources.</li>
      </ul>
    </div>
  );
}
